import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { LIMITED_NOTE, LIMITED_UNIT_NOTE } from "@/components/LimitedTag";
import { api, errMsg, toKSTInput, fromKSTInput, previewUrl, bonusPreviewUrl, FILL_RULES, DROP_STATUSES } from "@/lib/api";
import { UnitFancallFields } from "@/components/admin/UnitFancallFields";
import { BonusItemsFields, bonusInitial, bonusPayload } from "@/components/admin/BonusItemsFields";

const ROUND_TYPES = ["Lucky Draw", "PoB", "Fancall", "PoB Only", "Special"];
const ALBUM_TYPES = ["Regular", "Unit Digi", "Solo Digi", "Poca", "Other"];
const PAY_TO = ["ThePastelFix", "ArekuTrades"];

const Dropdown = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="bg-zinc-900"><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

const KSTInput = ({ value, onChange, testId }) => (
  <Input type="datetime-local" data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="bg-zinc-900 [color-scheme:dark]" />
);

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    {children}
    {hint && <div className="text-xs text-zinc-500">{hint}</div>}
  </div>
);

export default function DropFormDialog({ drop, onClose, onSaved }) {
  const isEdit = !!drop.id;
  const [f, setF] = useState({
    store: drop.store || "",
    ver_name: drop.ver_name || "",
    round_type: drop.round_type || "PoB",
    album_type: drop.album_type || "Regular",
    price_per_member: drop.price_per_member ?? "",
    price_won: drop.price_won ?? "",
    claims_close: toKSTInput(drop.claims_close),
    payment_due: toKSTInput(drop.payment_due),
    opens_at: toKSTInput(drop.opens_at),
    num_sets: drop.num_sets ?? 1,
    fill_rule: drop.fill_rule || "6/8",
    fancall_datetime: toKSTInput(drop.fancall_datetime),
    event_date: drop.event_date || "",
    passport_only: !!drop.passport_only,
    pay_to: drop.pay_to || "ThePastelFix",
    notes: drop.notes || "",
    units: drop.units?.length === 4 ? drop.units : ["", "", "", ""],
    status: drop.status || "open",
    limited: !!drop.limited,
    ...bonusInitial(drop),
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const isUnit = f.album_type === "Unit Digi";
  const isUnitFancall = isUnit && f.round_type === "Fancall";
  const setUnit = (i) => (e) => setF({ ...f, units: f.units.map((u, n) => (n === i ? e.target.value : u)) });
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : isEdit ? previewUrl(drop) : null), [file, drop, isEdit]);
  const [bonusFile, setBonusFile] = useState(null);
  const bonusPreview = useMemo(() => (bonusFile ? URL.createObjectURL(bonusFile) : isEdit ? bonusPreviewUrl(drop) : null), [bonusFile, drop, isEdit]);
  const upload = async (id, path, img, msg) => {
    const fd = new FormData();
    fd.append("file", img);
    try {
      await api.post(`/drops/${id}/${path}`, fd);
    } catch (upErr) {
      toast.error(errMsg(upErr, msg));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const body = {
      ...bonusPayload(f),
      claims_close: fromKSTInput(f.claims_close),
      payment_due: fromKSTInput(f.payment_due),
      opens_at: fromKSTInput(f.opens_at),
      fancall_datetime: fromKSTInput(f.fancall_datetime),
      num_sets: Number(f.num_sets),
      price_per_member: Number(f.price_per_member),
      price_won: f.price_won === "" || f.price_won === null ? null : Number(f.price_won),
      units: isUnit ? f.units : [],
      fill_rule: isUnit ? "unit" : f.fill_rule === "unit" ? "6/8" : f.fill_rule,
      limited: !!f.limited,
    };
    setBusy(true);
    try {
      const r = isEdit ? await api.put(`/drops/${drop.id}`, body) : await api.post("/drops", body);
      if (file) await upload(r.data.id, "preview", file, "Drop saved, but the set preview didn't upload");
      if (bonusFile && f.bonus_items) await upload(r.data.id, "bonus-preview", bonusFile, "Drop saved, but the bonus preview didn't upload");
      toast.success(isEdit ? "Drop updated" : "Drop created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't save drop"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="drop-form-dialog" className="bg-zinc-950 border-zinc-800 sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit drop" : "New drop"}</DialogTitle>
          <DialogDescription>{isUnit ? "Every set has 4 unit slots and must be 4/4 claimed to go through." : "Every set has 8 member slots. Claims beyond the set that's currently filling are pending."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Store Name"><Input data-testid="drop-store-input" required value={f.store} onChange={set("store")} className="bg-zinc-900" /></Field>
          <Field label="Ver Name"><Input data-testid="drop-ver-name-input" required value={f.ver_name} onChange={set("ver_name")} placeholder="e.g. Lucky Ver" className="bg-zinc-900" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Round Type"><Dropdown testId="drop-round-type-select" value={f.round_type} onChange={set("round_type")} options={ROUND_TYPES} /></Field>
            <Field label="Album Type"><Dropdown testId="drop-album-type-select" value={f.album_type} onChange={set("album_type")} options={ALBUM_TYPES} /></Field>
          </div>
          {isUnit && (
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 space-y-3" data-testid="drop-units-section">
              <div>
                <div className="text-sm font-semibold">Units for Claim</div>
                <div className="text-xs text-zinc-400 mt-0.5">These 4 units are the claim options. Each set must be 4/4 claimed before a new set opens, and claims are first come for all joiners.</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {f.units.map((u, i) => (
                  <Input key={i} data-testid={`drop-unit-input-${i + 1}`} required maxLength={60} value={u} onChange={setUnit(i)} placeholder={`Unit ${i + 1}`} className="bg-zinc-900" />
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="USD Price" hint={isUnit ? "Per unit" : "Per member"}><Input data-testid="drop-price-input" type="number" min="0" step="0.01" required value={f.price_per_member} onChange={set("price_per_member")} className="bg-zinc-900 font-mono" /></Field>
            <Field label="Won Price" hint={isUnit ? "Per unit (₩)" : "Per member (₩)"}><Input data-testid="drop-price-won-input" type="number" min="0" step="1" value={f.price_won} onChange={set("price_won")} className="bg-zinc-900 font-mono" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Sets" hint="Number of sets to fill"><Input data-testid="drop-sets-input" type="number" min="1" max="500" required value={f.num_sets} onChange={set("num_sets")} className="bg-zinc-900 font-mono" /></Field>
          </div>
          <Field label="Drop Opens (KST)" hint="Hidden from Open Drops until this time. Leave empty to show right away."><KSTInput testId="drop-opens-at-input" value={f.opens_at} onChange={set("opens_at")} /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Claims Close (KST)" hint="At this time an Open drop switches to Collecting Initials automatically."><KSTInput testId="drop-claims-close-input" value={f.claims_close} onChange={set("claims_close")} /></Field>
            <Field label="Payment Due (KST)"><KSTInput testId="drop-payment-due-input" value={f.payment_due} onChange={set("payment_due")} /></Field>
          </div>
          <Field label="Send Payments to"><Dropdown testId="drop-pay-to-select" value={f.pay_to} onChange={set("pay_to")} options={PAY_TO} /></Field>
          <Field label="Set Preview">
            <label className="relative flex items-center justify-center rounded-xl border-2 border-dashed border-zinc-700 hover:border-violet-500/60 transition-colors cursor-pointer h-40 overflow-hidden bg-zinc-900">
              <input type="file" accept="image/*" className="hidden" data-testid="drop-preview-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              {preview ? (
                <img src={preview} alt="Set preview" data-testid="drop-preview-image" className="absolute inset-0 h-full w-full object-contain" />
              ) : (
                <span className="text-center text-sm text-zinc-400"><ImagePlus className="h-6 w-6 mx-auto mb-1 text-zinc-500" />Upload set preview</span>
              )}
            </label>
          </Field>
          {!isUnit && <Field label="Set Fill Rules" hint="Each set is 8 members. Claims beyond the current filling set are set to pending.">
            <div className="grid gap-2" data-testid="drop-fill-rule-options">
              {Object.entries(FILL_RULES).filter(([, r]) => !r.hidden).map(([k, r]) => (
                <button
                  type="button"
                  key={k}
                  data-testid={`drop-fill-rule-${k.replace("/", "-")}`}
                  onClick={() => setF({ ...f, fill_rule: k })}
                  className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-200 ${f.fill_rule === k ? "border-pink-500 bg-pink-500/10" : "border-zinc-800 hover:border-zinc-600"}`}
                >
                  <span className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 ${f.fill_rule === k ? "border-pink-400 bg-pink-400 shadow-[inset_0_0_0_3px_#09090b]" : "border-zinc-600"}`} />
                  <span><span className="block text-sm font-semibold">{r.label}</span><span className="block text-xs text-zinc-400">{r.desc}</span></span>
                </button>
              ))}
            </div>
          </Field>}
          <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-red-500/30 bg-red-500/5 p-4" data-testid="drop-limited-field">
              <Checkbox data-testid="drop-limited-checkbox" checked={f.limited} onCheckedChange={(c) => setF({ ...f, limited: !!c })} className="mt-0.5" />
              <span>
                <span className="block text-sm font-medium">Limited Drop</span>
                <span className="block text-xs text-zinc-400">{isUnit ? LIMITED_UNIT_NOTE : LIMITED_NOTE}</span>
              </span>
            </label>
          {!isUnit && f.fill_rule === "fancall" && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-4" data-testid="drop-fancall-fields">
              <div className="text-xs text-amber-200/80">Joiners will be asked on the drop page whether they want to opt into the fancall.</div>
              <Field label="Fancall Date and Time (KST)"><KSTInput testId="drop-fancall-datetime-input" value={f.fancall_datetime} onChange={set("fancall_datetime")} /></Field>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Passport Only</div>
                  <div className="text-xs text-zinc-500">Shows a Passport Only notice on the drop page</div>
                </div>
                <Switch data-testid="drop-passport-only-switch" checked={f.passport_only} onCheckedChange={(c) => setF({ ...f, passport_only: c })} />
              </div>
            </div>
          )}
          {isUnitFancall && <UnitFancallFields f={f} setF={setF} />}
          <BonusItemsFields f={f} setF={setF} imageSrc={bonusPreview} onImage={setBonusFile} />
          <Field label="Additional Notes" hint="Optional. Shown to everyone on the drop page.">
            <Textarea data-testid="drop-notes-input" value={f.notes} onChange={set("notes")} maxLength={2000} rows={4} placeholder="Anything joiners should know about this drop" className="bg-zinc-900" />
          </Field>
          <Field label="Status" hint="Only Open drops take claims. Collecting Initials drops appear in joiners' Submit Payment list.">
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="drop-status-select" className="bg-zinc-900"><SelectValue /></SelectTrigger>
              <SelectContent>{DROP_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Button type="submit" disabled={busy} data-testid="drop-save-button" className="w-full h-11 rounded-xl bg-white text-zinc-950 hover:bg-zinc-200 font-semibold">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{isEdit ? "Save changes" : "Create drop"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
